import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import { requireAccess } from '../lib/guards';
import { Select } from '../components/ui/Select';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';
import type { AngebotStatus } from '../lib/api-enums';
import {
  ANGEBOT_STATUS_LABELS as STATUS_LABELS,
  ANGEBOT_STATUS_BADGE as STATUS_COLORS,
} from '../lib/api-labels';
import { formatGermanDateFromTimestamp } from '../lib/date-format';

interface Angebot {
  id: string;
  angebotsnummer: string | null;
  kundeId: string;
  titel: string;
  status: AngebotStatus;
  gueltigBis: string | null;
  summeBrutto: string;
  createdAt: string;
  versendetAm: string | null;
}

interface Kunde {
  id: string;
  kundennummer: string;
  firmenname: string;
}

export const Route = createFileRoute('/angebote/')({
  beforeLoad: () => requireAccess('/api/v1/angebote'),
  component: AngebotePage,
});

function AngebotePage() {
  const [statusFilter, setStatusFilter] = useState<'' | AngebotStatus>('');
  const [kundeFilter, setKundeFilter] = useState('');

  const kundenQ = useQuery({
    queryKey: ['kunden', 'lookup'],
    queryFn: () => api<{ items: Kunde[] }>('/api/v1/kunden'),
  });
  const listQ = useQuery({
    queryKey: ['angebote', statusFilter, kundeFilter],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (statusFilter) qs.set('status', statusFilter);
      if (kundeFilter) qs.set('kundeId', kundeFilter);
      const path = `/api/v1/angebote${qs.toString() ? '?' + qs.toString() : ''}`;
      return api<{ items: Angebot[] }>(path);
    },
  });

  const kundeMap = new Map((kundenQ.data?.items ?? []).map((k) => [k.id, k]));

  return (
    <div className="space-y-4">
      <PageHeader
        title="Angebote"
        description="Angebote an Kunden. Nummer wird erst beim Versenden vergeben; Entwürfe sind frei bearbeitbar."
        actions={
          <Link
            to="/angebote/neu"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium bg-brand-600 hover:bg-brand-700 text-white transition-colors"
          >
            + Neues Angebot
          </Link>
        }
      />

      <div className="flex items-center gap-3 text-sm flex-wrap">
        <label>
          <span className="text-slate-600 mr-2">Status</span>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as AngebotStatus | '')}
            className="w-40 inline-block"
          >
            <option value="">alle</option>
            {(Object.keys(STATUS_LABELS) as AngebotStatus[]).map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </Select>
        </label>
        <label>
          <span className="text-slate-600 mr-2">Kunde</span>
          <Select
            value={kundeFilter}
            onChange={(e) => setKundeFilter(e.target.value)}
            className="w-72 inline-block"
          >
            <option value="">alle</option>
            {(kundenQ.data?.items ?? []).map((k) => (
              <option key={k.id} value={k.id}>
                {k.kundennummer} — {k.firmenname}
              </option>
            ))}
          </Select>
        </label>
      </div>

      <ErrorBanner error={listQ.error} />

      {listQ.data && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="w-full">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left w-36">Nummer</th>
                <th className="px-4 py-2 text-left">Titel</th>
                <th className="px-4 py-2 text-left">Kunde</th>
                <th className="px-4 py-2 text-center w-28">Status</th>
                <th className="px-4 py-2 text-right w-32">Brutto</th>
                <th className="px-4 py-2 text-left w-28">Versendet</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {listQ.data.items.map((a) => {
                const k = kundeMap.get(a.kundeId);
                return (
                  <tr key={a.id} className="text-sm text-slate-800">
                    <td className="px-4 py-2 font-mono text-xs text-slate-600">
                      {a.angebotsnummer ?? <span className="text-slate-400 italic">Entwurf</span>}
                    </td>
                    <td className="px-4 py-2 font-medium">
                      <Link
                        to="/angebote/$id"
                        params={{ id: a.id }}
                        className="text-brand-700 hover:underline"
                      >
                        {a.titel}
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-slate-600">
                      {k ? `${k.kundennummer} — ${k.firmenname}` : a.kundeId.slice(0, 8)}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <span
                        className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[a.status]}`}
                      >
                        {STATUS_LABELS[a.status]}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {Number(a.summeBrutto).toLocaleString('de-DE', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{' '}
                      €
                    </td>
                    <td className="px-4 py-2 text-slate-600 text-xs">
                      {a.versendetAm ? formatGermanDateFromTimestamp(a.versendetAm) : '–'}
                    </td>
                  </tr>
                );
              })}
              {listQ.data.items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                    Keine Angebote zu diesem Filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
